import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight, Edit2, ArrowLeft, Star, Check, X,
  MessageSquare, Send, Save,
} from 'lucide-react';
import {
  getClinicLocationClientAccountById,
  getClinicLocationClientAccountsByLocation,
  createClinicLocationClientAccounts,
  updateClinicLocationClientAccount,
  setPrimaryLocationClinic,
  getLocationAccountNotes,
  createLocationAccountNote,
  deleteRelationshipNote,
} from '../services/clientClinicApi';
import { searchCustomers, getCustomerLocations } from '../services/customerApi';
import { clinicApi } from '@features/clinics/services/clinicApi';
import Autocomplete from '@shared/components/common/Autocomplete';
import { ServiceUseTypeLabels } from '../types/clientClinic';
import type {
  CreateClinicLocationClientAccountsBatchRequest,
  UpdateClinicLocationClientAccountRequest,
  Customer,
  CustomerLocation,
  AccountStatus,
  ServiceUseType,
  RelationshipNote,
} from '../types/clientClinic';
import type { ClinicListDto } from '@features/clinics/types/clinic';
import './ClientClinicRelationshipDetailPage.css';

type PageMode = 'view' | 'edit' | 'create';

function StatusBadge({ status }: { status: AccountStatus }) {
  const className = `status-badge status-${status.toLowerCase()}`;
  return <span className={className}>{status}</span>;
}

function ServiceTypeBadge({ serviceType }: { serviceType?: ServiceUseType }) {
  const key = serviceType || 'Clinical';
  const label = ServiceUseTypeLabels[key] || key;
  return (
    <span className={`service-type-badge service-type-${key.toLowerCase()}`}>
      {label}
    </span>
  );
}

export default function ClientClinicRelationshipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';
  const recordId = isNew ? null : Number(id);

  const [mode, setMode] = useState<PageMode>(isNew ? 'create' : 'view');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<CustomerLocation | null>(null);
  const [selectedClinic, setSelectedClinic] = useState<ClinicListDto | null>(null);
  const [customerLocations, setCustomerLocations] = useState<CustomerLocation[]>([]);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountStatus, setAccountStatus] = useState<AccountStatus>('Pending');
  // Edit-mode flags (a record is one service type)
  const [isPreferred, setIsPreferred] = useState(true);
  const [isPrimary, setIsPrimary] = useState(false);
  const [isPanelState, setIsPanelState] = useState(false);
  // Create-mode per-service-type sections (SLV2-2919: both types in one submit)
  const [includeClinical, setIncludeClinical] = useState(true);
  const [clinicalPreferred, setClinicalPreferred] = useState(true);
  const [clinicalPrimary, setClinicalPrimary] = useState(false);
  const [includeInjury, setIncludeInjury] = useState(false);
  const [injuryPreferred, setInjuryPreferred] = useState(true);
  const [injuryPrimary, setInjuryPrimary] = useState(false);
  const [injuryPanelState, setInjuryPanelState] = useState(false);

  // Notes state
  const [noteHistory, setNoteHistory] = useState<RelationshipNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [createNotes, setCreateNotes] = useState('');

  // Set Primary confirmation
  const [showPrimaryConfirm, setShowPrimaryConfirm] = useState(false);

  // Fetch record
  const {
    data: record,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['clinicLocationClientAccount', recordId],
    queryFn: () => getClinicLocationClientAccountById(recordId!),
    enabled: !!recordId,
  });

  // Sibling record: the OTHER service type of the same (location, clinic) pair.
  // A relationship is up to two records — one per service type — and each has
  // its own detail page; without this lookup the only way to reach the other
  // record is the badge back on the grid, which users don't discover.
  const { data: locationRecords = [] } = useQuery({
    queryKey: ['clinicLocationClientAccountSiblings', record?.customerLocationId],
    queryFn: () => getClinicLocationClientAccountsByLocation(record!.customerLocationId),
    enabled: !!record,
  });
  const siblingRecord = useMemo(
    () =>
      locationRecords.find(
        (r) => record && r.clinicId === record.clinicId && r.id !== record.id
      ),
    [locationRecords, record]
  );

  // Populate form from record
  useEffect(() => {
    if (record && mode !== 'create') {
      setAccountNumber(record.accountNumber || '');
      setAccountStatus(record.accountStatus);
      setIsPreferred(record.isPreferred);
      setIsPrimary(record.isPrimary);
      setIsPanelState(record.isPanelState);

      if (record.customerId && record.customerName) {
        setSelectedCustomer({ id: record.customerId, name: record.customerName });
      }
      if (record.clinicId && record.clinicName) {
        setSelectedClinic({
          id: record.clinicId,
          name: record.clinicName,
          code: record.clinicCode || '',
        } as ClinicListDto);
      }
      if (record.customerLocationId) {
        setSelectedLocation({
          id: record.customerLocationId,
          customerId: record.customerId,
          name: record.customerLocationName || '',
        });
      }
    }
  }, [record, mode]);

  // Load notes
  useEffect(() => {
    if (!recordId) return;
    setNotesLoading(true);
    getLocationAccountNotes(recordId)
      .then(setNoteHistory)
      .catch(() => setNoteHistory([]))
      .finally(() => setNotesLoading(false));
  }, [recordId]);

  // Load locations when customer changes (create mode)
  useEffect(() => {
    if (mode !== 'create') return;
    if (selectedCustomer) {
      getCustomerLocations(selectedCustomer.id)
        .then(setCustomerLocations)
        .catch(() => setCustomerLocations([]));
    } else {
      setCustomerLocations([]);
      setSelectedLocation(null);
    }
  }, [selectedCustomer, mode]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateClinicLocationClientAccountsBatchRequest) =>
      createClinicLocationClientAccounts(data),
    onSuccess: async (results) => {
      if (createNotes.trim()) {
        // Best-effort note-history entry on each created record
        await Promise.allSettled(
          results.map((r) => createLocationAccountNote(r.id, { noteText: createNotes.trim() }))
        );
      }
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccounts'] });
      navigate('/client-clinic-relationships');
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateClinicLocationClientAccountRequest }) =>
      updateClinicLocationClientAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccount', recordId] });
      setMode('view');
      setSuccess('Relationship updated successfully.');
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err: Error) => setError(err.message),
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (id: number) => setPrimaryLocationClinic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccount', recordId] });
      setShowPrimaryConfirm(false);
      setSuccess('Primary clinic updated.');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setShowPrimaryConfirm(false);
    },
  });

  const searchClinics = useCallback(async (query: string) => {
    const result = await clinicApi.getClinics(1, 20, query);
    return result.items;
  }, []);

  const handleAddNote = async () => {
    if (!newNoteText.trim() || !recordId) return;
    setAddingNote(true);
    try {
      const created = await createLocationAccountNote(recordId, { noteText: newNoteText.trim() });
      setNoteHistory((prev) => [created, ...prev]);
      setNewNoteText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteRelationshipNote(noteId);
      setNoteHistory((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    }
  };

  const handleSave = () => {
    setError(null);

    if (mode === 'create') {
      if (!selectedCustomer || !selectedLocation || !selectedClinic) {
        setError('Please select a customer, location, and clinic');
        return;
      }
      if (!includeClinical && !includeInjury) {
        setError('Select at least one service type (Clinical Services and/or Injury Management)');
        return;
      }
      createMutation.mutate({
        clinicId: selectedClinic.id,
        customerLocationId: selectedLocation.id,
        accountNumber,
        accountStatus,
        clinical: includeClinical
          ? { isPreferred: clinicalPreferred, isPrimary: clinicalPrimary }
          : undefined,
        injuryMgmt: includeInjury
          ? { isPreferred: injuryPreferred, isPrimary: injuryPrimary, isPanelState: injuryPanelState }
          : undefined,
        notes: createNotes,
      });
    } else if (mode === 'edit' && recordId) {
      updateMutation.mutate({
        id: recordId,
        data: {
          accountNumber,
          accountStatus,
          isPreferred,
          isPrimary,
          // Panel State only applies to InjuryMgmt records — never send it for
          // Clinical (the DB check constraint would reject a true value).
          ...(record?.serviceTypeKey === 'InjuryMgmt' ? { isPanelState } : {}),
        },
      });
    }
  };

  const handleCancel = () => {
    if (mode === 'create') {
      navigate('/client-clinic-relationships');
    } else {
      // Reset form to record values
      if (record) {
        setAccountNumber(record.accountNumber || '');
        setAccountStatus(record.accountStatus);
        setIsPreferred(record.isPreferred);
        setIsPrimary(record.isPrimary);
        setIsPanelState(record.isPanelState);
      }
      setError(null);
      setMode('view');
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Loading state
  if (!isNew && isLoading) {
    return (
      <div className="ccr">
        <div className="ccr-loading">
          <div className="loading-spinner" />
          <p>Loading relationship...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!isNew && (fetchError || !record)) {
    return (
      <div className="ccr">
        <div className="ccr-error">
          <p>{(fetchError as Error)?.message || 'Relationship not found'}</p>
          <Link to="/client-clinic-relationships" className="btn-secondary">Back to Relationships</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ccr">
      {/* Breadcrumb */}
      <div className="ccr-breadcrumb">
        <button
          type="button"
          className="ccr-breadcrumb-link"
          onClick={() => navigate('/client-clinic-relationships')}
        >
          Relationships
        </button>
        <ChevronRight size={14} className="ccr-breadcrumb-sep" />
        {isNew ? (
          <span className="ccr-breadcrumb-current">New Relationship</span>
        ) : (
          <span className="ccr-breadcrumb-current">
            {record?.clinicName} — {record?.customerLocationName}
          </span>
        )}
      </div>

      {/* Header */}
      <div className="ccr-header">
        <div className="ccr-header-main">
          <div className="ccr-header-left">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/client-clinic-relationships')}
              title="Back to list"
            >
              <ArrowLeft size={16} />
            </button>
            {isNew ? (
              <h1 className="ccr-header-title">New Location Relationship</h1>
            ) : (
              <>
                <h1 className="ccr-header-title">{record?.clinicName}</h1>
                <div className="ccr-header-badges">
                  <StatusBadge status={record!.accountStatus} />
                  <ServiceTypeBadge serviceType={record?.serviceTypeKey} />
                  {mode === 'view' && siblingRecord && (
                    <button
                      type="button"
                      className={`ccr-sibling-switch service-type-${siblingRecord.serviceTypeKey.toLowerCase()}`}
                      onClick={() => navigate(`/client-clinic-relationships/${siblingRecord.id}`)}
                      title={`This relationship also exists for ${ServiceUseTypeLabels[siblingRecord.serviceTypeKey]} — click to view and edit it`}
                    >
                      Switch to {ServiceUseTypeLabels[siblingRecord.serviceTypeKey]} →
                    </button>
                  )}
                  {mode === 'view' && record && !siblingRecord && (
                    <span className="ccr-sibling-hint">
                      No {record.serviceTypeKey === 'InjuryMgmt' ? 'Clinical Services' : 'Injury Management'} relationship
                      for this clinic &amp; location — create it via Add Relationship.
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          {!isNew && mode === 'view' && (
            <div className="ccr-header-actions">
              {record?.isPreferred && !record?.isPrimary && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPrimaryConfirm(true)}
                >
                  <Star size={16} />
                  Set as Primary
                </button>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={() => { setMode('edit'); setError(null); setSuccess(null); }}
              >
                <Edit2 size={16} />
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="ccr-body">
        {error && <div className="ccr-error-banner">{error}</div>}
        {success && <div className="ccr-success-banner">{success}</div>}

        {/* VIEW MODE */}
        {mode === 'view' && record && (
          <>
            <div className="ccr-section">
              <h2 className="ccr-section-title">Relationship Details</h2>
              <div className="ccr-detail-grid">
                <div className="ccr-field">
                  <span className="ccr-field-label">Customer</span>
                  <span className="ccr-field-value">{record.customerName}</span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Location</span>
                  <span className="ccr-field-value">{record.customerLocationName}</span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Clinic</span>
                  <span className="ccr-field-value">
                    {record.clinicName}
                    {record.clinicCode && ` (${record.clinicCode})`}
                  </span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Service Type</span>
                  <span className="ccr-field-value">
                    <ServiceTypeBadge serviceType={record.serviceTypeKey} />
                  </span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Account Number</span>
                  <span className={`ccr-field-value ${!record.accountNumber ? 'empty' : ''}`}>
                    {record.accountNumber || '—'}
                  </span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Status</span>
                  <span className="ccr-field-value">
                    <StatusBadge status={record.accountStatus} />
                  </span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Preferred</span>
                  <span className="ccr-field-value">
                    {record.isPreferred ? (
                      <span className="ccr-preferred-yes"><Check size={16} /> Yes</span>
                    ) : (
                      <span className="ccr-preferred-no">No</span>
                    )}
                  </span>
                </div>
                <div className="ccr-field">
                  <span className="ccr-field-label">Primary</span>
                  <span className="ccr-field-value">
                    {record.isPrimary ? (
                      <span className="ccr-primary-star"><Star size={16} fill="#f5a623" /> Yes</span>
                    ) : (
                      <span className="ccr-preferred-no">No</span>
                    )}
                  </span>
                </div>
                {record.serviceTypeKey === 'InjuryMgmt' && (
                  <div className="ccr-field">
                    <span className="ccr-field-label">Panel State Clinic</span>
                    <span className="ccr-field-value">
                      {record.isPanelState ? (
                        <span className="ccr-preferred-yes"><Check size={16} /> Yes</span>
                      ) : (
                        <span className="ccr-preferred-no">No</span>
                      )}
                    </span>
                  </div>
                )}
                {record.createdDate && (
                  <div className="ccr-field">
                    <span className="ccr-field-label">Created</span>
                    <span className="ccr-field-value">{formatDate(record.createdDate)}</span>
                  </div>
                )}
                {record.modifiedDate && (
                  <div className="ccr-field">
                    <span className="ccr-field-label">Last Modified</span>
                    <span className="ccr-field-value">{formatDate(record.modifiedDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes Section (always visible in view mode) */}
            <div className="ccr-section">
              <h2 className="ccr-section-title">Notes</h2>
              <div className="ccr-notes-section">
                <div className="ccr-notes-header">
                  <MessageSquare size={16} />
                  <span>Notes ({noteHistory.length})</span>
                </div>
                <div className="ccr-notes-add-row">
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                    className="ccr-notes-add-input"
                  />
                  <button
                    type="button"
                    className="btn-primary ccr-notes-add-btn"
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim() || addingNote}
                    title="Add note"
                  >
                    <Send size={14} />
                  </button>
                </div>
                <div className="ccr-notes-list">
                  {notesLoading ? (
                    <div className="ccr-notes-empty">Loading notes...</div>
                  ) : noteHistory.length === 0 ? (
                    <div className="ccr-notes-empty">No notes yet</div>
                  ) : (
                    noteHistory.map((note) => (
                      <div key={note.id} className={`ccr-note-item ${note.noteType === 'Inactivation' ? 'note-inactivation' : ''}`}>
                        <div className="ccr-note-item-header">
                          <span className="ccr-note-date">{formatDate(note.createdAt)}</span>
                          {note.noteType && (
                            <span className="ccr-note-type">{note.noteType}</span>
                          )}
                          <button
                            type="button"
                            className="ccr-note-delete-btn"
                            onClick={() => handleDeleteNote(note.id)}
                            title="Delete note"
                            aria-label="Delete note"
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <div className="ccr-note-text">{note.noteText}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* EDIT MODE */}
        {mode === 'edit' && record && (
          <div className="ccr-section">
            <h2 className="ccr-section-title">Edit Relationship</h2>
            <div className="ccr-edit-form">
              <div className="form-group">
                <label>Customer</label>
                <div className="ccr-readonly-value">{record.customerName}</div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <div className="ccr-readonly-value">{record.customerLocationName}</div>
              </div>
              <div className="form-group">
                <label>Clinic</label>
                <div className="ccr-readonly-value">
                  {record.clinicName}
                  {record.clinicCode && ` (${record.clinicCode})`}
                </div>
              </div>
              <div className="form-group">
                <label>Service Type</label>
                <div className="ccr-readonly-value">
                  {ServiceUseTypeLabels[record.serviceTypeKey] || record.serviceTypeKey}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="edit-account-number">Account Number</label>
                <input
                  id="edit-account-number"
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Enter account number..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-status">Status</label>
                <select
                  id="edit-status"
                  value={accountStatus}
                  onChange={(e) => setAccountStatus(e.target.value as AccountStatus)}
                >
                  <option value="Pending">Pending</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isPreferred}
                    onChange={(e) => {
                      setIsPreferred(e.target.checked);
                      if (!e.target.checked) setIsPrimary(false);
                    }}
                  />
                  Preferred Clinic
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isPrimary}
                    onChange={(e) => setIsPrimary(e.target.checked)}
                    disabled={!isPreferred}
                  />
                  Primary Clinic
                </label>
                {isPrimary && (
                  <div className="ccr-hint warning">
                    This will replace the current primary for this service type at this location.
                  </div>
                )}
              </div>
              {record.serviceTypeKey === 'InjuryMgmt' && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isPanelState}
                      onChange={(e) => setIsPanelState(e.target.checked)}
                    />
                    Panel State Clinic
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CREATE MODE */}
        {mode === 'create' && (
          <div className="ccr-section">
            <h2 className="ccr-section-title">New Relationship</h2>
            <div className="ccr-edit-form">
              <div className="form-group full-width">
                <Autocomplete<Customer>
                  label="Customer"
                  placeholder="Search customers..."
                  value={selectedCustomer}
                  onSelect={setSelectedCustomer}
                  searchFn={searchCustomers}
                  getDisplayValue={(c) => c.name}
                  getKey={(c) => c.id}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="create-location">
                  Location<span className="required">*</span>
                </label>
                <select
                  id="create-location"
                  value={selectedLocation?.id || ''}
                  onChange={(e) => {
                    const loc = customerLocations.find(
                      (l) => l.id === Number(e.target.value)
                    );
                    setSelectedLocation(loc || null);
                  }}
                  disabled={!selectedCustomer}
                  required
                >
                  <option value="">Select a location...</option>
                  {customerLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                      {loc.city && loc.state ? ` (${loc.city}, ${loc.state})` : ''}
                    </option>
                  ))}
                </select>
                {selectedCustomer && customerLocations.length === 0 && (
                  <div className="ccr-hint">No locations available for this customer</div>
                )}
              </div>

              <div className="form-group full-width">
                <Autocomplete<ClinicListDto>
                  label="Clinic"
                  placeholder="Search clinics..."
                  value={selectedClinic}
                  onSelect={setSelectedClinic}
                  searchFn={searchClinics}
                  getDisplayValue={(c) => `${c.name} (${c.code})`}
                  getKey={(c) => c.id}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="create-account-number">Account Number</label>
                <input
                  id="create-account-number"
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Enter account number..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="create-status">Status</label>
                <select
                  id="create-status"
                  value={accountStatus}
                  onChange={(e) => setAccountStatus(e.target.value as AccountStatus)}
                >
                  <option value="Pending">Pending</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* SLV2-2919: both service types can be created in one submit —
                  each gets its own Preferred/Primary pair; Panel State is an
                  Injury Management-only attribute. */}
              <div className="form-group full-width">
                <label>
                  Service Types<span className="required">*</span>
                </label>
                <div className="ccr-service-type-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeClinical}
                      onChange={(e) => setIncludeClinical(e.target.checked)}
                    />
                    Clinical Services
                  </label>
                  {includeClinical && (
                    <div className="ccr-service-type-flags">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={clinicalPreferred}
                          onChange={(e) => {
                            setClinicalPreferred(e.target.checked);
                            if (!e.target.checked) setClinicalPrimary(false);
                          }}
                        />
                        Preferred Clinic
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={clinicalPrimary}
                          onChange={(e) => setClinicalPrimary(e.target.checked)}
                          disabled={!clinicalPreferred}
                        />
                        Primary Clinic
                      </label>
                      {clinicalPrimary && (
                        <div className="ccr-hint warning">
                          This will replace the current primary Clinical Services clinic at this location.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="ccr-service-type-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeInjury}
                      onChange={(e) => setIncludeInjury(e.target.checked)}
                    />
                    Injury Management
                  </label>
                  {includeInjury && (
                    <div className="ccr-service-type-flags">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={injuryPreferred}
                          onChange={(e) => {
                            setInjuryPreferred(e.target.checked);
                            if (!e.target.checked) setInjuryPrimary(false);
                          }}
                        />
                        Preferred Clinic
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={injuryPrimary}
                          onChange={(e) => setInjuryPrimary(e.target.checked)}
                          disabled={!injuryPreferred}
                        />
                        Primary Clinic
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={injuryPanelState}
                          onChange={(e) => setInjuryPanelState(e.target.checked)}
                        />
                        Panel State Clinic
                      </label>
                      {injuryPrimary && (
                        <div className="ccr-hint warning">
                          This will replace the current primary Injury Management clinic at this location.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!includeClinical && !includeInjury && (
                  <div className="ccr-hint warning">Select at least one service type.</div>
                )}
              </div>

              <div className="form-group full-width">
                <label htmlFor="create-notes">Notes</label>
                <textarea
                  id="create-notes"
                  value={createNotes}
                  onChange={(e) => setCreateNotes(e.target.value)}
                  placeholder="Enter notes..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer (edit/create mode) */}
      {(mode === 'edit' || mode === 'create') && (
        <div className="ccr-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={16} />
            {isSaving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Set Primary Confirmation */}
      {showPrimaryConfirm && record && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !setPrimaryMutation.isPending) {
            setShowPrimaryConfirm(false);
          }
        }}>
          <div className="modal">
            <div className="modal-header">
              <h2>Set Primary Clinic</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowPrimaryConfirm(false)}
                disabled={setPrimaryMutation.isPending}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Set <strong>{record.clinicName}</strong> as the primary{' '}
                {(ServiceUseTypeLabels[record.serviceTypeKey] || 'clinical services').toLowerCase()} clinic for{' '}
                <strong>{record.customerLocationName}</strong>?
              </p>
              <p>This will replace the current primary clinic for this service type at this location.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowPrimaryConfirm(false)}
                disabled={setPrimaryMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setPrimaryMutation.mutate(record.id)}
                disabled={setPrimaryMutation.isPending}
              >
                {setPrimaryMutation.isPending ? 'Saving...' : 'Set Primary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
